/**
 * Color constants — the foundational Mainframe palette.
 *
 * Tunable as of 2026-06-03 (D-172, full unlock); reference: docs/ART_DIRECTION.md.
 *
 * Re-toned 2026-06-13 (S139 / BL-132 / D-237) to the owner-blessed
 * **"Glacier" pastel-neon direction** — cool near-black field + cool pastel
 * accents (mint-aqua / cool sky / cool violet / cool sand / cool rose /
 * cool mint). The dark cyberpunk terminal is preserved; the saturated neon
 * is softened into pastel-neon. See docs/design/PALETTE_PASTEL.md for the
 * concept + WCAG proof, and docs/session_139_pastel_swatch.html for the
 * mockups the owner signed off on. Identity contracts preserved:
 *  - D-224 ECHELON corruption stays a threatening rose/magenta family
 *    (muted, not de-tinted; the new muted rose hands to BL-128).
 *  - D-212 CRT/glitch identity untouched.
 *  - WCAG: every HUD-critical pair holds AA+ on near-black; the danger
 *    pairs (ORACLE, ECHELON SIGNAL, TEXT_SECONDARY) IMPROVE vs baseline.
 */
export const COLORS = {
  BG_PRIMARY: "#080C18",
  BG_GRID: "#040712",
  SENTINEL_GREEN: "#88E0D0",
  SENTINEL_GREEN_DIM: "#3A6868",
  ORACLE_RED: "#E89AB8",
  ORACLE_RED_DIM: "#7A3D58",
  AMBER_WARN: "#E8D098",
  ICE_CYAN: "#90C8F0",
  MALWARE_RED: "#E8A0B0",
  EMP_YELLOW: "#E8D098",
  TEXT_PRIMARY: "#B0E8E0",
  // S176 / BL-185: lifted from #508078 (4.37:1, sub-AA) to the minimal
  // luminance bump that clears WCAG AA-normal (≥4.5) on BG_PRIMARY #080C18 —
  // measures 4.91:1, same teal-green hue (HSL ~168°/23%/44% vs the old
  // 170°/23%/41%), still the "secondary/dim" role default (vs TEXT_PRIMARY's
  // 14.4:1), not washed out. Default colour of the caption + micro §9 roles.
  TEXT_SECONDARY: "#56897F",
  TEXT_GLITCH: "#FFFFFF",
  /**
   * BL-287 — the enemy conduit's green, promoted to a token at its EXACT shipped
   * value. Not a re-tone: `ProjectileRenderer` had this hardcoded as
   * `rgba(0, 255, 136, …)` at two call sites while the data packets on the same
   * object already used `SENTINEL_GREEN` — two greens on one object, one of them
   * a forked literal, and the only element on the board outside the palette.
   *
   * The obvious "fix" was to re-tone it to `SENTINEL_GREEN` like D-237 did
   * everywhere else. Owner decision 2026-08-12, and it is the better call:
   * *"#00FF88 nicht automatisch entfernen. Falls #00FF88 bewusst besser lesbar
   * ist als SENTINEL_GREEN, dann lieber als offiziellen zentralen Path-Token
   * definieren statt die Farbe aus Prinzip zu ändern."* The path is the one
   * element whose job is to be unmistakable at a glance, and legibility outranks
   * palette tidiness — so the value is preserved and the *drift* is what gets
   * fixed. Zero pixels move; there is now exactly one place to change it if the
   * device comparison ever says it should be cooler.
   */
  PATH_GREEN: "#00FF88",
} as const;

export type ColorKey = keyof typeof COLORS;

/** Duration of the dormant spawn threat pulse animation in ms */
export const THREAT_PULSE_DURATION = 1500;

/** Initial dormant spawn positions (col, row) — used by createGrid */
export const DORMANT_SPAWN_POSITIONS: readonly { col: number; row: number }[] =
  [
    { col: 7, row: 0 },
    { col: 7, row: 13 },
  ] as const;
